import React from 'react'
import type { Block } from '../../editor/types'

export type VideoFrame = 'none' | 'browser' | 'iphone' | 'laptop'

interface VideoBlockProps {
  block: Block
}

function getEmbedUrl(url: string): string | null {
  if (!url) return null

  // YouTube: various formats
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  )
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`

  return null
}

function VideoIframe({ embedUrl, aspectRatio }: { embedUrl: string; aspectRatio: string }) {
  return (
    <iframe
      src={embedUrl}
      className="w-full h-full"
      style={{ aspectRatio }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      loading="lazy"
    />
  )
}

function BrowserFrame({ embedUrl, aspectRatio }: { embedUrl: string; aspectRatio: string }) {
  return (
    <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200">
      <div className="bg-slate-100 px-4 py-2.5 flex items-center gap-3 border-b border-slate-200">
        <div className="flex gap-1.5">
          <div className="size-3 rounded-full bg-red-400" />
          <div className="size-3 rounded-full bg-amber-400" />
          <div className="size-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-slate-400 font-mono truncate border border-slate-200">
          youtube.com
        </div>
      </div>
      <VideoIframe embedUrl={embedUrl} aspectRatio={aspectRatio} />
    </div>
  )
}

function IPhoneFrame({ embedUrl }: { embedUrl: string }) {
  return (
    <div className="flex justify-center">
      <div className="relative w-[280px]">
        <div className="bg-[#1a1a1a] rounded-[3rem] p-[10px] shadow-2xl ring-1 ring-slate-700/50">
          <div className="absolute top-[18px] left-1/2 -translate-x-1/2 w-[90px] h-[24px] bg-[#1a1a1a] rounded-full z-10" />
          <div className="bg-black rounded-[2.25rem] overflow-hidden">
            <div className="h-14" />
            <div className="h-[480px] overflow-hidden">
              <VideoIframe embedUrl={embedUrl} aspectRatio="9/16" />
            </div>
            <div className="h-8 bg-black flex items-center justify-center">
              <div className="w-[100px] h-[4px] bg-slate-600 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LaptopFrame({ embedUrl, aspectRatio }: { embedUrl: string; aspectRatio: string }) {
  const screen = {
    left: (168 / 1200) * 100,
    top: (42 / 780) * 100,
    width: (864 / 1200) * 100,
    height: (600 / 780) * 100,
  }

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[600px] relative">
        <div
          className="absolute overflow-hidden rounded-[0.3%]"
          style={{
            left: `${screen.left}%`,
            top: `${screen.top}%`,
            width: `${screen.width}%`,
            height: `${screen.height}%`,
          }}
        >
          <VideoIframe embedUrl={embedUrl} aspectRatio={aspectRatio} />
        </div>
        <img src="/frames/laptop.svg" alt="" className="w-full block relative z-10 pointer-events-none" />
      </div>
    </div>
  )
}

export default function VideoBlock({ block }: VideoBlockProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const url = (config.url as string) || ''
  const aspectRatio = (config.aspectRatio as string) || '16/9'
  const frame = (config.frame as VideoFrame) || 'none'
  const embedUrl = getEmbedUrl(url)

  if (!embedUrl) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border-2 border-dashed border-slate-200 text-slate-400 text-sm"
        style={{ aspectRatio, maxHeight: 400 }}
      >
        {url ? 'Invalid video URL' : 'Paste a YouTube or Vimeo URL'}
      </div>
    )
  }

  switch (frame) {
    case 'browser':
      return <BrowserFrame embedUrl={embedUrl} aspectRatio={aspectRatio} />
    case 'iphone':
      return <IPhoneFrame embedUrl={embedUrl} />
    case 'laptop':
      return <LaptopFrame embedUrl={embedUrl} aspectRatio={aspectRatio} />
    default:
      return (
        <div className="rounded-lg overflow-hidden" style={{ aspectRatio }}>
          <VideoIframe embedUrl={embedUrl} aspectRatio={aspectRatio} />
        </div>
      )
  }
}
